/**
 * Specification Card Component
 *
 * Displays a single specification with metadata and actions.
 * Part of PowerSpec V3 - Spec-Driven Development
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState } from 'react';
import { Card, Button, Badge, Spinner } from 'flowbite-react';
import {
  HiDocumentText,
  HiEye,
  HiPencil,
  HiTrash,
  HiClock,
  HiCalendar,
  HiLink,
  HiCheckCircle
} from 'react-icons/hi';
import type { Specification } from './SpecList';

interface SpecCardProps {
  spec: Specification;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const SpecCard: React.FC<SpecCardProps> = ({ spec, onView, onEdit, onDelete }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete specification "${spec.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      onDelete();
    } catch (err) {
      console.error('[SpecCard] Error deleting spec:', err);
      alert(`Failed to delete specification: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'gray';
      case 'in_review':
        return 'warning';
      case 'approved':
        return 'success';
      case 'published':
        return 'info';
      default:
        return 'gray';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTimeSince = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return formatDate(dateString);
    }
  };

  return (
    <Card>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate text-lg">
              {spec.title}
            </h3>
            <p className="text-xs text-gray-500 font-mono mt-1 truncate">
              {spec.business_id}
            </p>
          </div>
          <HiDocumentText className="w-6 h-6 text-gray-400 ml-2 flex-shrink-0" />
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge color={getStatusBadgeColor(spec.status)}>
            {formatStatus(spec.status)}
          </Badge>
          {spec.version > 1 && (
            <Badge color="gray">
              v{spec.version}
            </Badge>
          )}
          {spec.approved_at && (
            <Badge color="success">
              <HiCheckCircle className="w-3 h-3 mr-1 inline" />
              Approved
            </Badge>
          )}
        </div>

        {/* Template Info */}
        <div className="mb-3 p-2 bg-gray-50 rounded border border-gray-200">
          <div className="text-xs text-gray-600">
            <div className="font-medium">{spec.template_name}</div>
            <div className="text-gray-500 capitalize">{spec.template_category}</div>
          </div>
        </div>

        {/* Tags */}
        {spec.tags && spec.tags.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {spec.tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
                >
                  {tag}
                </span>
              ))}
              {spec.tags.length > 3 && (
                <span className="text-xs text-gray-500 px-2 py-1">
                  +{spec.tags.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="text-xs text-gray-500 space-y-1 mb-3">
          <div className="flex items-center">
            <HiCalendar className="w-3 h-3 mr-1" />
            <span>Created: {formatDate(spec.created_at)}</span>
          </div>
          <div className="flex items-center">
            <HiClock className="w-3 h-3 mr-1" />
            <span>Updated: {getTimeSince(spec.updated_at)}</span>
          </div>
          <div className="flex items-center">
            <HiEye className="w-3 h-3 mr-1" />
            <span>{spec.view_count} view{spec.view_count !== 1 ? 's' : ''}</span>
          </div>
          {spec.connected_repo && (
            <div className="flex items-center">
              <HiLink className="w-3 h-3 mr-1" />
              <span className="truncate">Connected to repo</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 mt-auto">
          <Button
            size="sm"
            color="blue"
            onClick={onView}
          >
            <HiEye className="w-4 h-4 mr-2" />
            View
          </Button>
          <div className="flex gap-2">
            <Button
              size="sm"
              color="light"
              className="flex-1"
              onClick={onEdit}
            >
              <HiPencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              size="sm"
              color="failure"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Spinner size="sm" />
              ) : (
                <HiTrash className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SpecCard;
